import { Box, Typography } from '@mui/joy';
import { SxProps } from '@mui/material';
import { t } from 'i18next';
import { KeyboardEvent, SyntheticEvent } from 'react';

import { FormField } from 'core/components';
import { NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { IQueryPageData } from 'core/types';
import { partBomService } from 'partBoms/services';
import { IPartBom } from 'partBoms/types';
import { PartNumberHelper } from 'parts/helpers';
import { Color, Level } from 'theme/enums';

import { PopupIndicator } from './PopupIndicator';
import { descriptionStyle, endDecoratorStyle, inputStyle } from './style';

interface IProps {
  name: string;
  optionKey?: string;
  label?: string;
  placeholder?: string;
  onChange?: (event: SyntheticEvent, value: IPartBom) => void;
  onBlur?: (event: SyntheticEvent) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>, search: string) => void;
  endDecorator?: JSX.Element;
  onEndDecoratorClick?: (search?: string) => void;
  disabled?: boolean;
  listboxStyle?: SxProps;
  autocompleteStyle?: SxProps;
  requestPayload?: Record<string, unknown>;
}

const PAGE_SIZE = 10;

export const PartBomFormField = ({
  name,
  optionKey = 'number',
  label = '',
  placeholder = t('labelPartNumber'),
  onChange,
  onBlur,
  onKeyDown,
  endDecorator,
  onEndDecoratorClick,
  disabled = false,
  listboxStyle,
  autocompleteStyle,
  requestPayload = {},
  ...rest
}: IProps) => {
  const renderOptions = (option: IPartBom, _: (option: IPartBom) => string, inputValue: string) => {
    const hasNumber = option?.[optionKey]?.length > 0;

    const title =
      optionKey === 'number' ? PartNumberHelper.toLabel(option?.number) : option?.[optionKey];

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {hasNumber ? (
            <Typography level={Level.TITLE_SM}>
              {<TextHighlighter text={title} highlight={inputValue} />}
            </Typography>
          ) : null}
          <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
            <NeutralChip>
              <TextHighlighter text={option.bomType} highlight={inputValue} />
            </NeutralChip>
          </Typography>
        </Box>
        <Typography level={Level.BODY_XS}>
          <TextHighlighter
            text={option?.descriptionLong}
            highlight={inputValue}
            sx={descriptionStyle}
          />
        </Typography>
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {option?.manufacturerName}
        </Typography>
      </Box>
    );
  };

  const getNextPageParam = (lastPage: IQueryPageData, page: unknown[]) => {
    if (lastPage?.records?.length < PAGE_SIZE) {
      return undefined;
    }

    return page.length + 1;
  };

  return (
    <FormField
      name={name}
      type={InputType.AUTOCOMPLETE}
      inputProps={{
        label,
        optionKey,
        placeholder,
        endDecorator,
        onEndDecoratorClick,
        renderOptions,
        getOptions: partBomService.getPartBoms,
        requestPayload,
        queryOptions: { getNextPageParam },
        getKey: (option: IPartBom) => `${option?.number}-${option?.manufacturerId}`,
        getLabel: (option: IPartBom) => PartNumberHelper.toLabel(option?.number),
        onChange,
        onBlur,
        onKeyDown,
        slots: {
          popupIndicator: endDecorator ? null : PopupIndicator,
        },
        listboxStyle,
        disabled,
        inputStyle,
        autocompleteStyle,
        endDecoratorStyle,
        showPopupIndicator: !endDecorator,
        pageSize: PAGE_SIZE,
        applyDefaultQuery: true,
        ...rest,
      }}
    />
  );
};
